<template>
  <v-card class="asset-form">
    <v-card-title v-if="!update">Create a New Asset</v-card-title>
    <v-card-title v-else>Update {{ asset.title }}</v-card-title>

    <v-card-text>
      <v-alert
        type="error"
        v-if="errorMsg"
      >
        {{ errorMsg }}
      </v-alert>

      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
      >
        <v-col md="6">
          <!-- Visibility -->
          <v-checkbox
            v-model="asset.visible"
            label="Visible"
            class="mt-0"
          ></v-checkbox>

          <!-- Order -->
          <v-text-field
            v-model="asset.ordinal"
            label="Grid/List Order"
            type="number"
            clearable
            hint="Assets display first in grids/lists in ascending order if this is set. If null, they display beneath those with a value."
            persistent-hint
            class="mb-5"
          />

          <!-- Promoted -->
          <v-checkbox
            v-model="asset.promoted"
            label="Promoted to Front Page"
            class="mt-0"
          ></v-checkbox>
        </v-col>

        <!-- Sales Model -->
        <v-row>
          <v-col md="6">
            <v-select
              v-model="asset.salesModel"
              :items="salesModelItems"
              :rules="reqRules"
              label="Sales Model"
            ></v-select>
          </v-col>
        </v-row>

        <!-- Title -->
        <v-row>
          <v-col md="6">
            <v-text-field
              v-model="asset.title"
              label="Title"
            ></v-text-field>
          </v-col>

          <!-- Text Meta Fields -->
          <v-col
            v-for="(assetTypeMeta, at) in asset.assetType.metadataDefinitions.filter(t => t.type !== 'image')"
            :key="at"
            :md="assetTypeMeta.type === 'longText' ? '12' : '6'"
          >
            <component
              v-bind:is="metaFieldComponent[assetTypeMeta.type]"
              v-model="asset.metadata[assetTypeMeta.name]"
              :metadata="assetTypeMeta"
            >
            </component>
          </v-col>
        </v-row>

        <v-card
          tile
          class="mb-8"
        >
          <v-card-title>Selling</v-card-title>

          <v-card-text>
            <v-row>
              <v-col md="6">
                <!-- Cosignor Select -->
                <v-select
                  v-model="asset.consignorId"
                  :items="adminAvailableConsignors"
                  item-text="name"
                  item-value="id"
                  label="Consignor"
                  :rules="reqRules"
                  :disabled="asset.salesState !== 'DataEntry'"
                ></v-select>
              </v-col>

              <v-col md="6">
                <!-- Buy it now -->
                <v-checkbox
                  v-model="asset.allowBuyItNow"
                  label="Allow Buy It Now?"
                  class="mt-0"
                ></v-checkbox>
                <v-text-field
                  v-model.number="asset.buyItNowPrice"
                  label="Buy It Now Price"
                  prefix="$"
                  :rules="reqRules"
                  :disabled="asset.salesState !== 'DataEntry'"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Preferred Purchase -->
            <v-radio-group
              v-model="asset.preferredPurchaseSource"
              mandatory
            >
              <template v-slot:label>
                <div>Preferred Purchase Source</div>
              </template>
              <v-radio
                label="BCC"
                :value="0"
              ></v-radio>
              <v-radio
                label="Consignor"
                :value="1"
              ></v-radio>
            </v-radio-group>

            <!-- Auction -->
            <v-text-field
              v-model="asset.auctionLink"
              label="Auction Link"
            ></v-text-field>
            <v-menu
              ref="auctionStartMenu"
              :close-on-content-click="true"
              transition="scale-transition"
              offset-y
              min-width="auto"
              >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="auctionStartISODate"
                  label="Auction Start Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  ></v-text-field>
              </template>
              <v-date-picker
                no-title
                v-model="auctionStartISODate"
                @input="auctionStartMenu = false"
                ></v-date-picker>
            </v-menu>

          </v-card-text>
        </v-card>
      </v-form>
    </v-card-text>

    <v-card-actions
      class="mt-3"
    >
      <v-btn
        color="primary"
        :loading="loading"
        :disabled="loading"
        v-if="!update"
        @click="createSubmit"
      >
        <v-icon left>
          mdi-content-save
        </v-icon>
        Create
      </v-btn>
      <v-btn
        color="primary"
        :loading="loading"
        :disabled="loading"
        v-else
        @click="updateSubmit"
      >
        <v-icon left>
          mdi-content-save
        </v-icon>
        Update Asset
      </v-btn>
      <v-btn
        v-if="this.$route.name !== 'flipItemEdit'"
        @click="clearAndClose"
      >
        <v-icon left>
          mdi-cancel
        </v-icon>
        Cancel
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'
  import MetaFieldText from './MetaFieldText.vue'
  import MetaFieldLongText from './MetaFieldLongText.vue'
  import MetaFieldNumber from './MetaFieldNumber.vue'
  import MetaFieldCurrency from './MetaFieldCurrency.vue'

  export default {
    name: 'AssetForm',

    components: {
      MetaFieldText,
      MetaFieldLongText,
      MetaFieldNumber,
      MetaFieldCurrency
    },

    computed: {
      ...mapGetters([
        'adminAsset',
        'adminAssetType',
        'adminAvailableConsignors'
      ]),
    },

    data: () => ({
      // Form validation handling.
      loading: false,
      valid: true,
      errorMsg: '',
      salesModelItems: [
        {text:'Flip',value:'flip'},{text:'Hold',value:'hold'}
      ],
      reqRules: [
        v => !!v || 'This field is required.'
      ],

      // Auction start Date/Time handling.
      auctionStartMenu: false,
      auctionStartISODate: null,

      assetTypeId: 1, // car

      // Switch to inject metaField component names.
      metaFieldComponent: {
        text: 'MetaFieldText',
        longText: 'MetaFieldLongText',
        number: 'MetaFieldNumber',
        currency: 'MetaFieldCurrency',
        //choice: 'MetaFieldChoice',
        //flag: 'MetaFieldFlag'
      },

      // Default asset payload.
      asset: {
        assetTypeName: 'car',
        assetType: {
          metadataDefinitions: []
        },
        auctionLink: '',
        auctionStartTimestamp: 0,
        metadata: {},
        title: '',
        name: '',
        buyItNowPrice: null,
        // Static for now, this should be a selection, or keyed on user.
        consignorId: null,
        // Questions about if this is correct:
        salesModel: '',
        // possible values 'flip','hold'
        salesState: 'DataEntry',
        preferredPurchaseSource: 1,
        promoted: false,
        visible: true
      },

      // Action type.
      update: false
    }),

    methods: {
      ...mapActions([
        'createAsset',
        'updateAsset',
        'updatePurchaseSource',
        'getAssetType',
        'attachFile',
        'getAvailableConsignors',
      ]),

      clearAndClose: function () {
        if (this.$route.name === 'flipItemEdit') {
          // Go back to the asset detail view.
          this.$router.push({
            name: 'flipItem',
            params: this.$route.params
          })
        }
        else {
          // Close
          this.$emit('dialogClose')
        }
      },

      // Validation, and payload adjustments.
      prepareForm: function () {
        // Validate the form
        this.valid = this.$refs.form.validate()

        if (this.valid === true) {
          this.loading = true

          // Cleanup the payload.
          this.asset.auctionStartTimestamp = Date.parse(this.auctionStartISODate + 'T12:00:01')
          this.asset.name = this.asset.title.toLowerCase().replace(' ', '_')

          // Good to go.
          return true
        }

        return false
      },

      // Create an assset.
      createSubmit: function () {
        if (this.prepareForm()) {
          this.createAsset(this.asset).then(() => {
            this.clearAndClose()
          })
          .catch(() => {
            this.loading = false
            this.errorMsg = 'Something went wrong, please check your submission and try again.'
          })
        }
      },

      // Update an existing asset.
      updateSubmit: async function () {
        if (this.prepareForm()) {
          if (this.asset.preferredPurchaseSource !== this.adminAsset.preferredPurchaseSource) {
            try {
              await this.updatePurchaseSource({
                assetId: this.asset.id,
                purchaseSource: this.asset.preferredPurchaseSource
              })
            }
            catch (err) {
              console.error(err)
            }
          }

          await this.updateAsset(this.asset).then(() => {
            // Successfully updated.
            this.clearAndClose()
          }).catch(() => {
            this.loading = false
            this.errorMsg = 'Something went wrong, please check your submission and try again.'
          })
        }
      },

      // Bring the assetType metadata into the mix.
      typeInjection: async function () {
        // Make sure we have this asset type in state.
        await this.getAssetType(this.assetTypeId)

        this.asset.assetType = this.adminAssetType

        // Add blank metadata fields if we're creating.
        if (!this.update) {
          for(let m=0; m<this.adminAssetType.metadataDefinitions.length; m++) {
            this.asset.metadata[this.adminAssetType.metadataDefinitions[m].name] = ''
          }
        }

        // Set asset name.
        this.asset.assetTypeName = this.adminAssetType.name
      },

      // Are we updating?
      updateCheck: function () {
        this.loading = false

        // If adminAsset exists in state then we're updating.
        if (typeof this.adminAsset.id !== 'undefined') {
          this.update = true

          // Clone the asset data from state.
          this.asset = Object.assign({}, this.adminAsset)

          // Pull out the consignor id for the form.
          this.asset.consignorId = this.adminAsset.consignor.id

          // Some date handling for our date picker.
          console.log(this.asset.auctionStartTimestamp);
          if(this.asset.auctionStartTimestamp!== null) {
            this.auctionStartISODate = new Date(this.asset.auctionStartTimestamp).toISOString().split('T')[0]
          }
        }
        else {
          // In some cases the form is holding old data.
          // Try to force the data to clear.
          Object.assign(this.$data, this.$options.data.apply(this))
        }

        this.typeInjection()
      }
    },

    created() {
      // Grab our cosignors.
      this.getAvailableConsignors()

      this.updateCheck()
    },

    watch: {
      adminAsset: 'updateCheck'
    }
  }
</script>
